import { useParams, Link } from "react-router-dom";
import { animeList } from "@/mocks/animeData";
import { categories } from "@/mocks/categories";
import AnimeCard from "@/components/feature/AnimeCard";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const anime = animeList.find((a) => a.id === Number(id));

  if (!anime) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <span className="w-16 h-16 flex items-center justify-center mx-auto text-foreground-500">
            <i className="ri-error-warning-line text-4xl"></i>
          </span>
          <h2 className="text-xl font-bold text-foreground-200 mt-4">
            作品未找到
          </h2>
          <Link
            to="/"
            className="inline-block mt-4 text-sm text-primary-400 hover:text-primary-300 cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedAnime = animeList
    .filter((a) => a.categorySlug === anime.categorySlug && a.id !== anime.id)
    .slice(0, 5);

  const category = categories.find((c) => c.slug === anime.categorySlug);

  return (
    <main className="min-h-screen pt-20 pb-16 px-4 md:px-6">
      <div className="max-w-7xl mx-auto w-full">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            {
              label: category?.name || anime.category,
              href: `/category/${anime.categorySlug}`,
            },
            { label: anime.title },
          ]}
        />

        <article className="flex flex-col lg:flex-row gap-8 mt-6">
          <div className="lg:w-[300px] flex-shrink-0">
            <div className="rounded-xl overflow-hidden sticky top-24">
              <img
                src={anime.coverImage}
                alt={`${anime.title} - 风车动漫在线观看`}
                title={`${anime.title} - 风车动漫`}
                className="w-full aspect-[3/4] object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-100 mb-2">
              {anime.title}
            </h1>

            <div className="flex items-center gap-2 mb-4">
              <span className="flex items-center gap-1 text-accent-400">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-sm"></i>
                </span>
                <span className="text-sm font-bold">{anime.rating}</span>
              </span>
              <span className="text-foreground-600 text-xs">·</span>
              <span className="text-sm text-foreground-500">
                {anime.episodes}
              </span>
              <span className="text-foreground-600 text-xs">·</span>
              <span className="text-sm text-foreground-500">{anime.year}</span>
              <span className="text-foreground-600 text-xs">·</span>
              <span className="text-sm text-foreground-500">
                {anime.status}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {anime.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-800 text-foreground-400 text-xs rounded-full whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8 p-4 rounded-xl bg-background-800/40 border border-background-700/40">
              <div>
                <p className="text-xs text-foreground-600 mb-1">制作公司</p>
                <p className="text-sm text-foreground-300 font-medium">
                  {anime.studio}
                </p>
              </div>
              <div>
                <p className="text-xs text-foreground-600 mb-1">类型</p>
                <Link
                  to={`/category/${anime.categorySlug}`}
                  className="text-sm text-primary-400 hover:text-primary-300 font-medium cursor-pointer"
                >
                  {anime.category}
                </Link>
              </div>
              <div>
                <p className="text-xs text-foreground-600 mb-1">播出状态</p>
                <p className="text-sm text-foreground-300 font-medium">
                  {anime.status}
                </p>
              </div>
              <div>
                <p className="text-xs text-foreground-600 mb-1">集数</p>
                <p className="text-sm text-foreground-300 font-medium">
                  {anime.episodes}
                </p>
              </div>
              <div>
                <p className="text-xs text-foreground-600 mb-1">首播年份</p>
                <p className="text-sm text-foreground-300 font-medium">
                  {anime.year}
                </p>
              </div>
              <div>
                <p className="text-xs text-foreground-600 mb-1">更新日期</p>
                <p className="text-sm text-foreground-300 font-medium">
                  {anime.updateDate}
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h2 className="text-lg font-bold font-heading text-foreground-200 mb-3">
                作品简介
              </h2>
              <p className="text-sm text-foreground-500 leading-relaxed">
                {anime.description}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-950 font-semibold rounded-full whitespace-nowrap cursor-pointer transition-all hover:scale-105 text-sm flex items-center justify-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-play-circle-line"></i>
                </span>
                开始观看
              </button>
              <button className="px-6 py-3 bg-background-800 hover:bg-background-700 text-foreground-300 font-medium rounded-full whitespace-nowrap cursor-pointer transition-all text-sm flex items-center justify-center gap-2 border border-background-600/50">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-heart-line"></i>
                </span>
                加入收藏
              </button>
            </div>
          </div>
        </article>

        {relatedAnime.length > 0 && (
          <section className="mt-16">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-200 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
              {relatedAnime.map((item) => (
                <AnimeCard key={item.id} anime={item} size="sm" />
              ))}
            </div>
          </section>
        )}
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: `${anime.title} - 风车动漫在线观看`,
            description: anime.description,
            image: anime.coverImage,
            datePublished: `${anime.year}-01-01`,
            dateModified: anime.updateDate,
            author: {
              "@type": "Organization",
              name: "风车动漫",
            },
            publisher: {
              "@type": "Organization",
              name: "风车动漫",
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://fengchedongman.com/article/${anime.id}`,
            },
          }),
        }}
      />
    </main>
  );
}